import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { proxiedImageUrl } from '../../../config/images';
import './InlineCandidateList.css';

export interface PickerItem {
    /** Unique within the list - used as the React key and passed back on select. */
    key: string;
    /** Primary text (match mode) - unused in image mode. */
    label?: string;
    /** Secondary text under label, e.g. "1999 · Album" or "Group · US". */
    sublabel?: string;
    /** Present for image-mode items - renders a thumbnail instead of a text row. */
    thumbnailUrl?: string;
    /** Set when thumbnailUrl is already one of our own API endpoints (e.g. the
     *  album's stored cover art) rather than an external host. Such URLs must
     *  NOT be sent through the image proxy: the proxy fetches external images
     *  server-side, and handing it a local /api/... URL would make the backend
     *  fetch itself. Defaults to false, i.e. treat as external. */
    thumbnailIsLocal?: boolean;
    /** Groups items under a heading (e.g. "Photo" / "Backdrop") - items with the
     *  same groupLabel render together, in first-seen order. */
    groupLabel?: string;
    /** Marks the candidate currently in use, so the grid shows which artwork
     *  is already applied rather than presenting every option as equal. */
    selected?: boolean;
}

interface Props {
    /** 'list' for match candidates (text rows), 'grid' for image candidates
     *  (thumbnails) - explicit rather than inferred from the items, since an
     *  image-mode list can legitimately contain zero items with a real
     *  thumbnailUrl (e.g. only an "embedded picture" candidate, which has
     *  none) and still needs to render as a grid, not a text list. */
    variant: 'list' | 'grid';
    items: PickerItem[];
    loading: boolean;
    error: string | null;
    emptyMessage: string;
    applyingKey: string | null;
    onSelect: (item: PickerItem) => void;
}

// Renders a match/artwork candidate list inline, inside the edit panel's
// active tab body - no overlay/portal, unlike the old stacked
// CandidatePickerModal this replaces (the panel now shows one tool's
// content at a time in place, per the tabbed edit-panel rework).
export function InlineCandidateList({
    variant,
    items,
    loading,
    error,
    emptyMessage,
    applyingKey,
    onSelect,
}: Props): React.JSX.Element {
    // Candidate thumbnails hit external hosts by way of the image proxy -
    // that fetch can fail (host unreachable, rate-limited, etc). Track which
    // tiles failed so they fall back to the same placeholder icon used for
    // "no thumbnail at all", instead of sitting there as a blank box with
    // only the alt text visible, which reads as broken/unfinished.
    const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());

    const isImageMode = variant === 'grid';

    const groups: [string | undefined, PickerItem[]][] = [];
    for (const item of items) {
        const existing = groups.find(([label]) => label === item.groupLabel);
        if (existing) {
            existing[1].push(item);
        } else {
            groups.push([item.groupLabel, [item]]);
        }
    }

    return (
        <div className="inlinePicker">
            {loading && <div className="inlinePickerStatus">Loading candidates…</div>}
            {!loading && error && <div className="inlinePickerStatus inlinePickerStatusError">{error}</div>}
            {!loading && !error && items.length === 0 && <div className="inlinePickerStatus">{emptyMessage}</div>}

            {!loading &&
                !error &&
                groups.map(([groupLabel, groupItems]) => (
                    <div className="inlinePickerGroup" key={groupLabel ?? '__default'}>
                        {groupLabel && <div className="inlinePickerGroupLabel">{groupLabel}</div>}
                        <div className={isImageMode ? 'inlinePickerGrid' : 'inlinePickerList'}>
                            {groupItems.map((item) =>
                                isImageMode ? (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className={
                                            item.selected
                                                ? 'inlinePickerTile inlinePickerTileSelected'
                                                : 'inlinePickerTile'
                                        }
                                        // aria-pressed rather than aria-selected:
                                        // these are buttons, not listbox options.
                                        aria-pressed={item.selected ?? false}
                                        disabled={applyingKey !== null}
                                        onClick={() => onSelect(item)}
                                    >
                                        {item.thumbnailUrl && !failedThumbnails.has(item.key) ? (
                                            <img
                                                className="inlinePickerTileImg"
                                                src={
                                                    item.thumbnailIsLocal
                                                        ? item.thumbnailUrl
                                                        : proxiedImageUrl(item.thumbnailUrl)
                                                }
                                                alt=""
                                                onError={() =>
                                                    setFailedThumbnails((prev) => new Set(prev).add(item.key))
                                                }
                                            />
                                        ) : (
                                            <div className="inlinePickerTilePlaceholder">
                                                <ImageOff size={20} strokeWidth={2} />
                                            </div>
                                        )}
                                        <span className="inlinePickerTileLabel">
                                            {applyingKey === item.key ? 'Applying…' : item.sublabel}
                                        </span>
                                    </button>
                                ) : (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className="inlinePickerRow"
                                        disabled={applyingKey !== null}
                                        onClick={() => onSelect(item)}
                                    >
                                        <span className="inlinePickerRowLabel">{item.label}</span>
                                        {item.sublabel && <span className="inlinePickerRowSublabel">{item.sublabel}</span>}
                                        {applyingKey === item.key && <span className="inlinePickerRowApplying">Applying…</span>}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                ))}
        </div>
    );
}
